<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { navMenuItems, brandName } from '../../config/nav.js';
import logoImg from '../../../../assets/images/logo.png';

const activeNavId = ref(null);

// 从path中提取sectionId
const getSectionIdFromPath = (path) => {
  const hashMatch = path.match(/#(\d+)/);
  return hashMatch ? hashMatch[1] : null;
};

// 更新激活状态
const updateActiveNav = () => {
  const hash = window.location.hash;
  if (hash) {
    activeNavId.value = hash.replace('#', '');
  } else {
    // 如果没有hash，检查是否是主页
    const homeItem = navMenuItems.find((item) => item.path === '/');
    if (homeItem) {
      activeNavId.value = null;
    }
  }
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    // 考虑固定导航栏的高度
    const navHeight = 40;
    const elementPosition = element.offsetTop;
    const offsetPosition = elementPosition - navHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

const handleNavClick = (path) => {
  // 解析路径中的 hash
  const sectionId = getSectionIdFromPath(path);
  if (sectionId) {
    // 更新激活状态
    activeNavId.value = sectionId;
    // 更新 URL hash
    window.location.hash = `#${sectionId}`;
    // 滚动到对应位置
    setTimeout(() => {
      scrollToSection(sectionId);
    }, 100);
  } else if (path === '/') {
    // 主页
    activeNavId.value = null;
    window.location.hash = '';
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

// 检查导航项是否激活
const isActive = (path) => {
  const sectionId = getSectionIdFromPath(path);
  if (sectionId) {
    return activeNavId.value === sectionId;
  } else if (path === '/') {
    return activeNavId.value === null;
  }
  return false;
};

onMounted(() => {
  // 页面加载时检查 URL 中的 hash，如果有则滚动到对应位置
  const handleHashScroll = () => {
    updateActiveNav();
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      nextTick(() => {
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 100);
      });
    }
  };

  // 根据滚动位置更新激活状态
  const handleScroll = () => {
    const navHeight = 40;
    const scrollPosition = window.scrollY + navHeight + 50; // 50px偏移量，提前激活

    // 检查各个section的位置
    const sections = ['1', '2', '3', '4', '5'];
    let currentSection = null;

    for (let i = sections.length - 1; i >= 0; i--) {
      const sectionId = sections[i];
      const element = document.getElementById(sectionId);
      if (element && element.offsetTop <= scrollPosition) {
        currentSection = sectionId;
        break;
      }
    }

    // 如果找到了section，更新激活状态（但不改变hash，避免影响浏览器历史）
    if (currentSection && activeNavId.value !== currentSection) {
      activeNavId.value = currentSection;
    } else if (!currentSection && activeNavId.value !== null) {
      // 如果滚动到顶部，清除激活状态
      if (window.scrollY < 100) {
        activeNavId.value = null;
      }
    }
  };

  // 初始化激活状态
  updateActiveNav();
  handleHashScroll();

  // 监听 hash 变化（浏览器前进后退）
  window.addEventListener('hashchange', handleHashScroll);

  // 监听滚动事件
  window.addEventListener('scroll', handleScroll);

  // 保存清理函数
  window._hashChangeHandler = handleHashScroll;
  window._scrollHandler = handleScroll;
});

onUnmounted(() => {
  if (window._hashChangeHandler) {
    window.removeEventListener('hashchange', window._hashChangeHandler);
    delete window._hashChangeHandler;
  }
  if (window._scrollHandler) {
    window.removeEventListener('scroll', window._scrollHandler);
    delete window._scrollHandler;
  }
});
</script>

<template>
  <nav class="pc-nav">
    <div class="nav-container">
      <div class="nav-left">
        <img
          :src="logoImg"
          alt="logo"
          class="logo"
        />
        <span class="brand-name">{{ brandName }}</span>
      </div>
      <div class="nav-right">
        <a
          v-for="item in navMenuItems"
          :key="item.path"
          href="#"
          :class="['nav-item', { 'nav-item-active': isActive(item.path) }]"
          @click.prevent="handleNavClick(item.path)"
        >
          {{ item.label }}
        </a>
      </div>
    </div>
  </nav>
</template>

<style scoped lang="scss">
.pc-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  position: relative;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  width: 25px;
  height: 25px;
  object-fit: contain;
}

.brand-name {
  font-size: 10px;
  //   font-weight: 500;
  color: #000000;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 15px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.nav-item {
  font-size: 8px;
  color: #000000;
  text-decoration: none;
  transition: background-color 0.3s ease;
  white-space: nowrap;
  font-weight: 500;
  padding: 3px 5px;
  border-radius: 10px;

  &:hover {
    background-color: #e5ad3e;
  }

  &.nav-item-active {
    background-color: #e5ad3e;
  }
}
</style>
